import React, { useState, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Client } from '@/types/client';
import { getClientById } from '@/lib/apiService';
import { showErrorToast, showWarningToast } from '@/utils/toast-utils';
import { DietaryIntakeGoals, DietaryIntakeGoalsRef } from '@/clients/details/legacy/components/DietaryIntakeGoals';
import { MealTypeConfig, MealTypesIntegrated, MealTypesIntegratedRef } from '@/components/clients/meal-settings/MealTypesIntegrated';
import MealPlanScheduleFields from '@/components/meal-planning/MealPlanScheduleFields';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateMealPlanCalorieOverride } from '@/utils/meal-plan-calorie-override-validation';

const MAX_PLAN_LENGTH_DAYS = 7;
const DAY_IN_MS = 1000 * 60 * 60 * 24;

const DEFAULT_FORM_VALUES = {
  carbs: 45,
  protein: 25,
  fat: 30,
  macronutrientsSpread: 100,
  mealPrepTime: 30,
  planName: '',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: new Date(Date.now() + (MAX_PLAN_LENGTH_DAYS - 1) * DAY_IN_MS).toISOString().slice(0, 10),
};


interface ClientState {
  selectedClient: Client | null;
  clientDetails: Client | null;
  loadingClientDetails: boolean;
}

type DietaryGoalsSnapshot = {
  targetCalories?: number | string | null;
  proteinMin?: number | string | null;
  proteinMax?: number | string | null;
  carbsMin?: number | string | null;
  carbsMax?: number | string | null;
  fatMin?: number | string | null;
  fatMax?: number | string | null;
  eerGoalCalories?: number | string | null;
  proteinGoalMin?: number | string | null;
  proteinGoalMax?: number | string | null;
  carbsGoalMin?: number | string | null;
  carbsGoalMax?: number | string | null;
  fatGoalMin?: number | string | null;
  fatGoalMax?: number | string | null;
  allergies?: string[];
  intolerances?: string[];
  foodIntolerances?: string[];
  cuisineType?: string[];
  cuisineTypes?: string[];
  preferences?: string[];
};

export interface NewClientIntakeData {
  clientId: string;
  clientName: string;
  planName: string;
  // Meal Type Section
  mealType: 'pre-breakfast-snack' | 'breakfast' | 'pre-lunch-snack' | 'lunch' | 'pre-dinner-snack' | 'dinner' | 'other';
  otherMealType?: string;
  kcalSpread: {
    preBreakfastSnack: number;
    breakfast: number;
    preLunchSnack: number;
    lunch: number;
    preDinnerSnack: number;
    dinner: number;
    other: number;
  };
  mealPrepTime: number; // in minutes
  // Dietary Intake Goals Section (combined Energy + Macro)
  kcalPerDay: number;
  kcalSpreadTotal: number;
  carbs: number; // percentage
  protein: number; // percentage
  fat: number; // percentage
  macronutrientsSpread: number; // percentage
  startDate: string;
  endDate: string;
  dietaryGoals?: DietaryGoalsSnapshot;
  mealTypes?: MealTypeConfig[];
}
interface MealPlanApiPayload {
  type: string;
  action: string;
  clientId: string;
  name?: string;
  startDate: string;
  days: number;
  overrideClientGoals?: {
    eerGoalCalories: number | string | null;
    proteinGoalMin: number | string | null;
    proteinGoalMax: number | string | null;
    carbsGoalMin: number | string | null;
    carbsGoalMax: number | string | null;
    fatGoalMin: number | string | null;
    fatGoalMax: number | string | null;
    allergies?: string[];
    intolerances?: string[];
    cuisineTypes?: string[];
    preferences?: string[];
    dietaryPreferences?: string[];
  };
  overrideMealProgram?: {
    meals: Array<{
      mealOrder: number;
      mealName: string;
      mealTime: string;
      targetCalories?: number;
      mealType: MealTypeConfig['mealType'];
    }>;
  };
  allergies: string[];
  intolerances: string[];
  cuisineTypes: string[];
  preferences: string[];
  dietaryPreferences: string[];
}

interface NewClientIntakeFormProps {
  onSubmit: (data: MealPlanApiPayload) => void;
  loading?: boolean;
  initialData?: Partial<NewClientIntakeData>;
  dateConflictMessage?: string | null;
}

const confirmCalorieOverrideForMealPlan = (client: Client, targetCalories: unknown): boolean => {
  const validation = validateMealPlanCalorieOverride(client, targetCalories);
  if (validation.status === 'ok') return true;

  if (validation.status === 'error') {
    showErrorToast(new Error(validation.message), 'Validation Error');
    return false;
  }

  return window.confirm(`${validation.message}\n\nDo you want to continue creating this meal plan?`);
};

const NewClientIntakeForm: React.FC<NewClientIntakeFormProps> = ({
  onSubmit,
  loading = false,
  initialData,
  dateConflictMessage = null,
}) => {
  // Consolidated state management
  const [clientState, setClientState] = useState<ClientState>({
    selectedClient: null,
    clientDetails: null,
    loadingClientDetails: false,
  });


  const [showOtherMealType, setShowOtherMealType] = useState(false);

  // Destructure for easier access
  const { selectedClient, clientDetails, loadingClientDetails } = clientState;
  
  // Ref for dietary goals component
  const dietaryGoalsRef = useRef<DietaryIntakeGoalsRef>(null);
  // Ref for meal types component
  const mealTypesRef = useRef<MealTypesIntegratedRef>(null);
  const [mealTypesValid, setMealTypesValid] = React.useState(true);
  const {
    register,
    handleSubmit,
    control,
    formState: {
      errors
    },
    watch,
    setValue,
    setError,
    getValues,
  } = useForm<NewClientIntakeData>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      ...initialData
    }
  });
  const watchedMealType = watch('mealType');

  React.useEffect(() => {
    if (!dateConflictMessage) return;
    setError('startDate', { type: 'server', message: dateConflictMessage });
    setError('endDate', { type: 'server', message: dateConflictMessage });
  }, [dateConflictMessage, setError]);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  
  React.useEffect(() => {
    setShowOtherMealType(watchedMealType === 'other');
  }, [watchedMealType]);

  const clearSelectedClient = React.useCallback(() => {
    setClientState({
      selectedClient: null,
      clientDetails: null,
      loadingClientDetails: false,
    });
    setValue('clientId', '');
    setValue('clientName', '');
  }, [setValue]);

  const hydrateSelectedClient = React.useCallback(
    async (clientId: string, fallbackClient?: Client) => {
      if (!clientId) {
        clearSelectedClient();
        return;
      }

      setClientState((prev) => ({
        ...prev,
        selectedClient: fallbackClient ?? prev.selectedClient,
        loadingClientDetails: true,
      }));

      setValue('clientId', clientId, { shouldValidate: true });
      if (fallbackClient) {
        setValue('clientName', `${fallbackClient.firstName} ${fallbackClient.lastName}`.trim());
      }

      try {
        const response = await getClientById(clientId);
        const detailedClient = response.client as Client | undefined;
        const nextClient = detailedClient ?? fallbackClient ?? null;

        setClientState({
          selectedClient: nextClient,
          clientDetails: nextClient,
          loadingClientDetails: false,
        });

        if (nextClient) {
          setValue('clientName', `${nextClient.firstName} ${nextClient.lastName}`.trim());
        }
      } catch (error) {
        console.error('Error fetching client details:', error);

        if (fallbackClient) {
          setClientState({
            selectedClient: fallbackClient,
            clientDetails: fallbackClient,
            loadingClientDetails: false,
          });
          setValue('clientName', `${fallbackClient.firstName} ${fallbackClient.lastName}`.trim());
        } else {
          setClientState((prev) => ({
            ...prev,
            clientDetails: null,
            loadingClientDetails: false,
          }));
          showErrorToast(error, "Warning");
        }
      }
    },
    [clearSelectedClient, setValue]
  );

  React.useEffect(() => {
    const initialClientId = initialData?.clientId?.toString().trim();
    if (!initialClientId) {
      return;
    }

    if (clientDetails?.id?.toString?.() === initialClientId) {
      return;
    }

    void hydrateSelectedClient(initialClientId);
  }, [initialData?.clientId, clientDetails?.id, hydrateSelectedClient]);

  // Helper function to validate meal types (all or none should have target kcal)
  const validateMealTypes = React.useCallback((): boolean => {
    if (!mealTypesRef.current) return true; // If no meal types component, validation passes
    
    const mealTypes = mealTypesRef.current.getCurrentMealTypes();
    if (mealTypes.length === 0) return true; // No meal types, validation passes
    
    const mealsWithKcal = mealTypes.filter(meal => 
      meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
    );
    const mealsWithoutKcal = mealTypes.filter(meal => 
      meal.targetKcal === null || meal.targetKcal === undefined || meal.targetKcal <= 0
    );
    
    // Return true if all have kcal OR all don't have kcal (no mixing)
    return !(mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0);
  }, []);

  // Periodically check meal types validation (when component updates)
  React.useEffect(() => {
    const interval = setInterval(() => {
      const isValid = validateMealTypes();
      setMealTypesValid(isValid);
    }, 500); // Check every 500ms

    return () => clearInterval(interval);
  }, [clientDetails, validateMealTypes]); // Re-run when client changes

  const handleClientSelect = async (clientId: string, client: Client) => {
    if (!clientId) {
      clearSelectedClient();
      return;
    }

    await hydrateSelectedClient(clientId, client);
  };

  const onFormSubmit = async (data: NewClientIntakeData) => {
    // Validate meal types before form submission (double-check)
    if (!validateMealTypes()) {
      showWarningToast("Validation Error", "Either all meal slots should have target calories or none of them should. Please provide values for all meals or leave all empty.");
      return; // Stop form submission
    }
    
    // Get dietary goals from the component (without saving)
    let dietaryGoals: DietaryGoalsSnapshot = {};
    if (dietaryGoalsRef.current && clientDetails) {
      try {
        // Get current dietary goals from the component without saving
        const goalsData = dietaryGoalsRef.current.getCurrentValues();
        dietaryGoals = goalsData || {};
      } catch (error) {
        console.error('Error getting dietary goals:', error);
        // Continue with empty dietary goals if there's an error
      }
    }

    if (
      clientDetails &&
      !confirmCalorieOverrideForMealPlan(
        clientDetails,
        dietaryGoals.targetCalories ?? dietaryGoals.eerGoalCalories
      )
    ) {
      return;
    }

    // Get meal types from the component (without saving)
    let mealTypes: MealTypeConfig[] = [];
    if (mealTypesRef.current && clientDetails) {
      try {
        // Get current meal types from the component without saving
        const mealTypesData = mealTypesRef.current.getCurrentMealTypes();
        mealTypes = mealTypesData || [];
      } catch (error) {
        console.error('Error getting meal types:', error);
        // Continue with empty meal types if there's an error
      }
    }
    
    // Calculate days based on selected date range
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    
    // Ensure we always have at least 1 day
    let days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // If days is 0 or negative (same date or invalid), default to 1
    if (days <= 0 || isNaN(days)) {
      days = 1;
    }


    
    const apiPayload: MealPlanApiPayload = {
      type: "meal-plan",
      action: "save",
      clientId: data.clientId,
      startDate: data.startDate,
      days: days,
      allergies: dietaryGoals.allergies || [],
      intolerances: dietaryGoals.intolerances || dietaryGoals.foodIntolerances || [],
      cuisineTypes: dietaryGoals.cuisineType || [],
      preferences: dietaryGoals.preferences || [],
      dietaryPreferences: dietaryGoals.preferences || []
    };
    if (data.planName?.trim()) {
      apiPayload.name = data.planName.trim();
    }

    // Only include override client goals if values have been changed from original
    // Use legacy clientGoal shape (flat) for comparison; API may still return it
    const originalGoals = clientDetails && (clientDetails as { clientGoal?: typeof dietaryGoals }).clientGoal;
    if (dietaryGoals && originalGoals) {
      const original = originalGoals;
      const current = dietaryGoals;

      // Check if any values have actually changed (including dietary preferences)
      const hasChanges = (
        (current.targetCalories && current.targetCalories !== original.eerGoalCalories) ||
        (current.proteinMin && current.proteinMin !== original.proteinGoalMin) ||
        (current.proteinMax && current.proteinMax !== original.proteinGoalMax) ||
        (current.carbsMin && current.carbsMin !== original.carbsGoalMin) ||
        (current.carbsMax && current.carbsMax !== original.carbsGoalMax) ||
        (current.fatMin && current.fatMin !== original.fatGoalMin) ||
        (current.fatMax && current.fatMax !== original.fatGoalMax) ||
        // Check dietary preferences
        (current.allergies && current.allergies.length > 0) ||
        (current.cuisineTypes && current.cuisineTypes.length > 0) ||
        (current.preferences && current.preferences.length > 0)
      );

      if (hasChanges) {
        apiPayload.overrideClientGoals = {
          eerGoalCalories: current.targetCalories || original?.eerGoalCalories || null,
          proteinGoalMin: current.proteinMin || original?.proteinGoalMin || null,
          proteinGoalMax: current.proteinMax || original?.proteinGoalMax || null,
          carbsGoalMin: current.carbsMin || original?.carbsGoalMin || null,
          carbsGoalMax: current.carbsMax || original?.carbsGoalMax || null,
          fatGoalMin: current.fatMin || original?.fatGoalMin || null,
          fatGoalMax: current.fatMax || original?.fatGoalMax || null,
          // Include dietary preferences in override
          allergies: current.allergies || [],
          intolerances: current.intolerances || current.foodIntolerances || [],
          cuisineTypes: current.cuisineTypes || [],
          preferences: current.preferences || [],
          dietaryPreferences: current.preferences || []
        };
      }
    }

    // Include meal types if they exist (in Auto Generate context, any configured meal types are overrides)
    if (mealTypes.length > 0) {
      const hasMissingRequired = mealTypes.some(meal => !meal.name?.trim() || !meal.timeSlot?.trim() || !meal.mealType);
      if (hasMissingRequired) {
        showWarningToast(
          'Validation Error',
          'Please ensure all meal slots have a Name, Type, and Time filled out.'
        );
        return;
      }

      // Validation Rule: Either ALL meals should have targetKcal OR NONE should have it (no mixing)
      const mealsWithKcal = mealTypes.filter(meal => 
        meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
      );
      const mealsWithoutKcal = mealTypes.filter(meal => 
        meal.targetKcal === null || meal.targetKcal === undefined || meal.targetKcal <= 0
      );

      // Check if there's a mix (some have values, some don't)
      if (mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0) {
        console.error('Validation failed: Mixed target calories detected');
        showWarningToast("Validation Error", "Either all meal slots should have target calories or none of them should. Please provide values for all meals or leave all empty.");
        return; // Stop form submission
      }
      
      apiPayload.overrideMealProgram = {
        meals: mealTypes.map((meal, index) => {
          return {
            mealOrder: index + 1,
            mealName: meal.name,
            mealTime: meal.timeSlot,
            mealType: meal.mealType,
            ...(meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0
              ? { targetCalories: meal.targetKcal }
              : {}),
          };
        })
      };
    }

    onSubmit(apiPayload);
  };
  const scheduleTip = (
    <p className="text-sm text-blue-900">
      <strong>💡 Tip:</strong> Auto-generated meal plans can cover up to 7 days. Select a date range within one week.
    </p>
  );

  return <div className=" mx-auto space-y-8">
      <div className="space-y-2">
        <Label htmlFor="planName">Plan Name</Label>
        <Input
          id="planName"
          placeholder="e.g. Week 1 Fat Loss Plan"
          {...register('planName')}
        />
      </div>

      <MealPlanScheduleFields
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        tipContent={scheduleTip}
        maxPlanLengthDays={MAX_PLAN_LENGTH_DAYS}
        todayDate={today}
        onClientSelected={handleClientSelect}
      />

      {/* Dietary Goals Section - Using same component as client details */}
      {clientDetails && (
        <DietaryIntakeGoals
          ref={dietaryGoalsRef}
          clientId={clientDetails.id}
          client={clientDetails}
          hideSaveButtons={true}
          refreshClientData={async () => {
            // Refresh client data if needed
            if (selectedClient?.id) {
              await handleClientSelect(selectedClient.id.toString(), selectedClient);
            }
          }}
        />
      )}

      {/* Meal Types Section - Using same component as client details */}
      {clientDetails && (
        <MealTypesIntegrated
          ref={mealTypesRef}
          clientId={clientDetails.id}
          clientName={`${clientDetails.firstName} ${clientDetails.lastName}`}
          client={clientDetails}
          className="border-t pt-6"
          hideSaveButtons={true}
        />
      )}

     

      {/* Generate Button */}
      <div className="flex justify-center">
        <Button 
          onClick={handleSubmit(onFormSubmit)} 
          className="px-8 py-3 text-base" 
          disabled={loading || loadingClientDetails || !clientDetails || !mealTypesValid} 
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Meal Plan...
            </>
          ) : loadingClientDetails ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading Client Details...
            </>
          ) : !clientDetails ? (
            'Select a Client First'
          ) : (
            'Generate Meal Plan'
          )}
        </Button>
      </div>
    </div>;
};
export default NewClientIntakeForm;
